import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { berserkr, isSupabaseConfigured } from "../lib/supabase";

export interface JustificationCited { defense_id: string; instrument_type: string; title: string }
export interface JustificationElement {
  id: string;
  element_id: string;
  element_name: string;
  verdict: string;                  // excused | not_excused | unclear | not_applicable | error
  stage: string | null;            // facial | merits_sj | na
  strength: string | null;
  rationale: string | null;
  cited: JustificationCited[] | null;
  proposer_verdict: string | null;
  judge_verdict: string | null;
  review_status: string | null;     // agreed | split
  human_assessment: string | null;  // excused | partial | not_excused | null
  human_note: string | null;
  needs_review: boolean;
}
export interface JustificationClaim {
  claim_id: string;
  claim_name: string;
  target: string | null;
  total: number;
  excused: number;
  unclear: number;
  not_excused: number;
  reviewed: number;
  elements: JustificationElement[];
}

/** Layer 4: per claim -> element -> justification verdict (latest build). */
export function useCaseJustification(caseId: string | undefined) {
  return useQuery<JustificationClaim[], Error>({
    queryKey: ["case-justification", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    staleTime: 60 * 1000,
    queryFn: async () => {
      if (!caseId) return [];
      const { data, error } = await berserkr().rpc("fetch_case_justification", { p_case_id: caseId });
      if (error) throw new Error(error.message);
      return (data as JustificationClaim[]) ?? [];
    },
  });
}

/** Record the human's 3-way call on one element: excused | partial | not_excused. */
export function useSetJustificationAssessment(caseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; assessment: string; note?: string }) => {
      const { error } = await berserkr().rpc("set_element_justification_assessment", {
        p_id: args.id, p_assessment: args.assessment, p_note: args.note ?? null,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["case-justification", caseId] }),
  });
}
